import React, { useState, useMemo } from 'react';
import { Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useBrands } from '@/hooks/useBrands';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { subDays, parseISO, isValid } from 'date-fns';
import { useOrganization } from '@/contexts/OrganizationContext';

import { useCampaigns } from '../../hooks/useCampaigns';
import { CampaignStatCards } from './CampaignStatCards';
import { CampaignFiltersBar } from './CampaignFiltersBar';
import { CampaignTableView } from './CampaignTableView';
import { CampaignPagination } from './CampaignPagination';
import CampaignCreationForm from '@/components/CampaignCreationForm';

export const CampaignsPageView: React.FC = () => {
  const [showCreationForm, setShowCreationForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [selectedOwner, setSelectedOwner] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { toast } = useToast();
  const { currentOrganization } = useOrganization();
  const isBrandOrg = currentOrganization?.organization_type === 'brand';

  const { data: campaigns = [], isLoading: campaignsLoading, error: campaignsError, refetch } = useCampaigns();
  const { data: brands = [] } = useBrands();

  // Unique owners list for filter dropdown
  const ownersList = useMemo(() => {
    const ownersSet = new Set<string>();
    campaigns.forEach((c) => {
      if (c.owner_name && c.owner_name !== 'Unassigned') {
        ownersSet.add(c.owner_name);
      }
    });
    return Array.from(ownersSet);
  }, [campaigns]);

  // Stat card metrics
  const statCounts = useMemo(() => {
    let active = 0;
    let completed = 0;
    let draft = 0;

    campaigns.forEach((c) => {
      const st = (c.status || '').trim().toLowerCase();
      if (st === 'active') active++;
      else if (st === 'completed') completed++;
      else if (st === 'draft' || !st) draft++;
    });

    return {
      total: campaigns.length,
      active,
      completed,
      draft,
    };
  }, [campaigns]);

  // Filter campaigns based on search term, brand, owner, and date range
  const filteredCampaigns = useMemo(() => {
    if (!campaigns) return [];

    return campaigns.filter((campaign) => {
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase();
        const matchesName = campaign.name?.toLowerCase().includes(searchLower);
        const matchesBrand = campaign.brand?.toLowerCase().includes(searchLower);
        const matchesStatus = campaign.status?.toLowerCase().includes(searchLower);
        const matchesId = campaign.id?.toLowerCase().includes(searchLower);
        if (!matchesName && !matchesBrand && !matchesStatus && !matchesId) {
          return false;
        }
      }

      if (!isBrandOrg && selectedBrand !== 'all') {
        if (campaign.brand !== selectedBrand && campaign.brand_name !== selectedBrand) {
          return false;
        }
      }

      if (selectedOwner !== 'all') {
        if (campaign.owner_name !== selectedOwner) {
          return false;
        }
      }

      if (dateRange !== 'all') {
        const days = Number(dateRange);
        if (!isNaN(days) && days > 0) {
          const dateToTest = campaign.created_at || campaign.start_date;
          if (!dateToTest) return false;
          const parsed = parseISO(dateToTest);
          if (!isValid(parsed)) return false;
          const cutoff = subDays(new Date(), days);
          if (parsed < cutoff) return false;
        }
      }

      return true;
    });
  }, [campaigns, searchTerm, selectedBrand, selectedOwner, dateRange, isBrandOrg]);

  // Paginate filtered campaigns for Table View
  const paginatedCampaigns = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredCampaigns.slice(start, start + pageSize);
  }, [filteredCampaigns, currentPage, pageSize]);

  const hasActiveFilters =
    searchTerm !== '' ||
    (!isBrandOrg && selectedBrand !== 'all') ||
    selectedOwner !== 'all' ||
    dateRange !== 'all';

  const handleResetFilters = () => {
    setSearchTerm('');
    setSelectedBrand('all');
    setSelectedOwner('all');
    setDateRange('all');
    setCurrentPage(1);
  };

  const handleCampaignSubmit = (campaignData: any) => {
    toast({
      title: 'Campaign Created Successfully',
      description: `${campaignData.name} has been created.`,
    });
    setShowCreationForm(false);
    refetch();
  };

  if (campaignsLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner size="medium" label="Loading campaigns..." />
        </div>
      </div>
    );
  }

  if (campaignsError) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="text-center py-16 bg-white rounded-xl border border-red-100 p-8 shadow-sm">
          <div className="text-red-600 font-semibold mb-2">Error loading campaigns</div>
          <p className="text-gray-500 text-sm mb-4">An error occurred while loading your campaign data.</p>
          <Button onClick={() => window.location.reload()} variant="default">
            Try again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-6 px-6 mx-auto">
      {/* Header Row */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Campaigns</h1>
          <p className="text-sm text-gray-500 mt-1">Manage and track all your campaigns in one place.</p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={() => setShowCreationForm(true)} className="gap-2 shrink-0 font-medium">
            <Plus className="w-4 h-4" />
            <span>New Campaign</span>
          </Button>
        </div>
      </div>

      {/* KPI Stat Cards */}
      <CampaignStatCards
        totalCount={statCounts.total}
        activeCount={statCounts.active}
        completedCount={statCounts.completed}
        draftCount={statCounts.draft}
      />

      {/* Filters and Controls */}
      <CampaignFiltersBar
        searchTerm={searchTerm}
        onSearchChange={(val) => {
          setSearchTerm(val);
          setCurrentPage(1);
        }}
        selectedBrand={selectedBrand}
        onBrandChange={(val) => {
          setSelectedBrand(val);
          setCurrentPage(1);
        }}
        brandsList={brands}
        selectedOwner={selectedOwner}
        onOwnerChange={(val) => {
          setSelectedOwner(val);
          setCurrentPage(1);
        }}
        ownersList={ownersList}
        dateRange={dateRange}
        onDateRangeChange={(val) => {
          setDateRange(val);
          setCurrentPage(1);
        }}
        onResetFilters={handleResetFilters}
        hasActiveFilters={hasActiveFilters}
        isBrandOrg={isBrandOrg}
      />

      {/* Empty Search / Filter Results State */}
      {filteredCampaigns.length === 0 && (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200/80 shadow-sm mb-6">
          <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-gray-800 mb-1">No campaigns found</h3>
          <p className="text-sm text-gray-500 mb-4">
            {hasActiveFilters
              ? 'No campaigns match your current filters and search criteria.'
              : "You haven't created any campaigns yet."}
          </p>
          {hasActiveFilters ? (
            <Button variant="link" onClick={handleResetFilters} className="text-purple-600 font-medium p-0 h-auto">
              Clear filters and search
            </Button>
          ) : (
            <Button onClick={() => setShowCreationForm(true)} className="gap-2">
              <Plus className="w-4 h-4" />
              <span>Create First Campaign</span>
            </Button>
          )}
        </div>
      )}

      {/* Main Content: Table View */}
      {filteredCampaigns.length > 0 && (
        <>
          <CampaignTableView campaigns={paginatedCampaigns} />
          <CampaignPagination
            totalItems={filteredCampaigns.length}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
          />
        </>
      )}

      {/* Creation Modal */}
      {showCreationForm && (
        <CampaignCreationForm
          onClose={() => setShowCreationForm(false)}
          onSubmit={handleCampaignSubmit}
        />
      )}
    </div>
  );
};

export default CampaignsPageView;
